import type { Attachment, Author, AuthorPaginator, AuthorQueryOptions, AuthResponse, CategoryPaginator, CategoryQueryOptions, ChangePasswordUserInput, CheckoutVerificationInput, CouponPaginator, CouponQueryOptions, CreateAbuseReportInput, CreateContactUsInput, CreateFeedbackInput, CreateOrderInput, CreateOrderReviewInput, CreateQuestionInput, CreateRefundInput, CreateReviewInput, DownloadableFilePaginator, Feedback, ForgotPasswordUserInput, LoginUserInput, Manufacturer, ManufacturerPaginator, ManufacturerQueryOptions, MyQuestionQueryOptions, MyReportsQueryOptions, Order, OrderPaginator, OrderQueryOptions, OrderReview, OtpLoginInputType, OTPResponse, PasswordChangeResponse, PopularProductQueryOptions, Product, PurchasedProductPaginator, IBuyTogetherProduct, ProductPaginator, ProductQueryOptions, QueryOptions, QuestionPaginator, QuestionQueryOptions, Refund, RefundPaginator, ResetPasswordOptionsResponse, ResetPasswordUserInput, SendResetPasswordInput, ValidateResetTokenInput, Review, ReviewPaginator, ReviewQueryOptions, ReviewResponse, SendOtpCodeInputType, Settings, Shop, ShopPaginator, ShopQueryOptions, SocialLoginInputType, GoogleMobileLoginInputType, TagPaginator, TagQueryOptions, Type, TypeQueryOptions, UpdateReviewInput, UpdateUserInput, User, VerifiedCheckoutData, VerifyCouponInputType, VerifyCouponResponse, VerifyForgotPasswordUserInput, VerifyOtpInputType, Wishlist, WishlistPaginator, WishlistQueryOptions, GetParams, SettingsQueryOptions, CreateOrderPaymentInput, PaymentIntentCollection, BestSellingProductQueryOptions, UpdateEmailUserInput, EmailChangeResponse, ValidateResetTokenResponse, VerificationEmailUserInput, StoreNoticeQueryOptions, StoreNoticePaginator, FAQS, FaqsQueryOptions, FaqsPaginator, ShopMapLocation, RefundQueryOptions, RefundReasonPaginator, TermsAndConditionsQueryOptions, TermsAndConditionsPaginator, FlashSaleQueryOptions, FlashSalePaginator, FlashSale, RefundPolicyPaginator, RefundPolicyQueryOptions, FlashSaleProductsQueryOptions, NotifyLogsQueryOptions, NotifyLogsPaginator, NotifyLogs, BecomeSeller, ShopMaintenanceEvent, RegisterUserOutput, ProductPrice, CreateUserRelationshipInput, ICreditCard, AppleMobileLoginPayload, AppleMobileLoginResponse } from '@/types';
import type {
  InstitutionalPage,
  InstitutionalPagePaginator,
} from '@/types/institutional-page';
import type {
  CreateDeliveryPartnerInput,
  CreateDeliveryPartnerResponse,
  DeliveryPartnerFormOptions,
} from '@/types/delivery-partner';
import { API_ENDPOINTS } from './api-endpoints';
import { HttpClient } from './http-client';
//@ts-ignore
import { OTPVerifyResponse } from '@/types';
import type { AvailableCouponsResponse } from '@/types';
import axios from 'axios';

class Client {
  products = {
    all: ({
      type,
      categories,
      name,
      shop_id,
      author,
      manufacturer,
      min_price,
      max_price,
      tags,
      ...params
    }: Partial<ProductQueryOptions>) =>
      HttpClient.get<ProductPaginator>(API_ENDPOINTS.PRODUCTS, {
        searchJoin: 'and',
        with: 'type;types;product_status',
        ...params,
        search: HttpClient.formatSearchParams({
          type,
          categories,
          name,
          shop_id,
          author,
          manufacturer,
          min_price,
          max_price,
          tags,
          // @ts-ignore
          ['product_status.name']: 'publish',
        }),
      }),
    popular: (params: Partial<PopularProductQueryOptions>) =>
      HttpClient.get<Product[]>(API_ENDPOINTS.PRODUCTS_POPULAR, params),

    bestSelling: (params: Partial<BestSellingProductQueryOptions>) =>
      HttpClient.get<Product[]>(API_ENDPOINTS.BEST_SELLING_PRODUCTS, params),

    questions: ({ question, ...params }: QuestionQueryOptions) =>
      HttpClient.get<QuestionPaginator>(API_ENDPOINTS.PRODUCTS_QUESTIONS, {
        searchJoin: 'and',
        ...params,
        search: HttpClient.formatSearchParams({
          question,
        }),
      }),

    get: ({ slug, language }: GetParams) =>
      HttpClient.get<Product>(`${API_ENDPOINTS.PRODUCTS}/${slug}`, {
        language,
        searchJoin: 'and',
        with: 'categories;shop;type;types;variations;variations.attribute.values;variation_options;tags',
      }),

    buyTogether: (productId: number) =>
      HttpClient.get<IBuyTogetherProduct | null>(
        `${API_ENDPOINTS.PRODUCTS}/${productId}/buy-together`,
      ),

    createFeedback: (input: CreateFeedbackInput) =>
      HttpClient.post<Feedback>(API_ENDPOINTS.FEEDBACK, input),
    createAbuseReport: (input: CreateAbuseReportInput) =>
      HttpClient.post<Review>(
        API_ENDPOINTS.PRODUCTS_REVIEWS_ABUSE_REPORT,
        input,
      ),
    createQuestion: (input: CreateQuestionInput) =>
      HttpClient.post<Review>(API_ENDPOINTS.PRODUCTS_QUESTIONS, input),
    getProductsByFlashSale: ({ slug, language }: GetParams) => {
      return HttpClient.get<Product>(
        `${API_ENDPOINTS.PRODUCTS_BY_FLASH_SALE}`,
        {
          language,
          slug,
        },
      );
    },
  };
  productPrice = {
    get: ({ search, include }: { search: string; include?: string }) =>
      HttpClient.get<ProductPrice[]>(`${API_ENDPOINTS.PRODUCTS_PRICE}`, {
        search,
        with: include,
      }),
  };
  productVariantGroups = {
    getByProductId: (productId: string | number) =>
      HttpClient.get<{
        id: number;
        name: string;
        variant_type: string;
        custom_type_label?: string;
        items: Array<{
          product_id: number;
          label: string;
          slug: string;
          sku: string;
          name: string;
          in_stock: boolean;
          sort_order: number;
        }>;
      } | null>(
        `${API_ENDPOINTS.PRODUCT_VARIANT_GROUPS}/by-product/${productId}`,
      ),
  };
  productCharacteristics = {
    get: ({
      id,
      search,
      include,
    }: {
      id: string | number;
      search?: string;
      include?: string;
    }) =>
      HttpClient.get(`${API_ENDPOINTS.PRODUCTS}/${id}/characteristics`, {
        search,
        with: include,
      }),
  };
  productCharacteristicValues = {
    get: ({ search, include }: { search?: string; include?: string }) =>
      HttpClient.get<any[]>(
        `${API_ENDPOINTS.PRODUCTS_CHARACTERISTICS_VALUES}`,
        {
          search,
          with: include,
        },
      ),
  };
  characteristicGroups = {
    get: ({ search, include }: { search?: string; include?: string }) =>
      HttpClient.get(`${API_ENDPOINTS.CHARACTERISTICS_GROUPS}`, {
        search,
        with: include,
      }),
  };
  characteristics = {
    get: ({ search, include }: { search?: string; include?: string }) =>
      HttpClient.get<any[]>(`${API_ENDPOINTS.CHARACTERISTICS}`, {
        search,
        with: include,
      }),
    getById: ({ id }: { id: number }) =>
      HttpClient.get(`${API_ENDPOINTS.CHARACTERISTICS}/${id}`),
  };
  myQuestions = {
    all: (params: MyQuestionQueryOptions) =>
      HttpClient.get<QuestionPaginator>(API_ENDPOINTS.MY_QUESTIONS, {
        with: 'user',
        orderBy: 'created_at',
        sortedBy: 'desc',
        ...params,
      }),
  };
  myReports = {
    all: (params: MyReportsQueryOptions) =>
      HttpClient.get<QuestionPaginator>(API_ENDPOINTS.MY_REPORTS, {
        with: 'user',
        orderBy: 'created_at',
        sortedBy: 'desc',
        ...params,
      }),
  };
  reviews = {
    all: ({ rating, ...params }: ReviewQueryOptions) =>
      HttpClient.get<ReviewPaginator>(API_ENDPOINTS.PRODUCTS_REVIEWS, {
        searchJoin: 'and',
        with: 'user',
        ...params,
        search: HttpClient.formatSearchParams({
          rating,
        }),
      }),
    get: ({ id }: { id: string }) =>
      HttpClient.get<Review>(`${API_ENDPOINTS.PRODUCTS_REVIEWS}/${id}`),
    create: (input: CreateReviewInput) =>
      HttpClient.post<ReviewResponse>(API_ENDPOINTS.PRODUCTS_REVIEWS, input),
    update: (input: UpdateReviewInput) =>
      HttpClient.put<ReviewResponse>(
        `${API_ENDPOINTS.PRODUCTS_REVIEWS}/${input.id}`,
        input,
      ),
  };
  categories = {
    all: ({ type, ...params }: Partial<CategoryQueryOptions>) =>
      HttpClient.get<CategoryPaginator>(API_ENDPOINTS.CATEGORIES, {
        searchJoin: 'and',
        ...params,
        ...(type && {
          search: HttpClient.formatSearchParams({ 'types.slug': type }),
        }),
      }),
  };
  tags = {
    all: ({ type, ...params }: Partial<TagQueryOptions>) =>
      HttpClient.get<TagPaginator>(API_ENDPOINTS.TAGS, {
        searchJoin: 'and',
        ...params,
        ...(type && { search: HttpClient.formatSearchParams({ type }) }),
      }),
  };
  types = {
    all: (params?: Partial<TypeQueryOptions>) =>
      HttpClient.get<Type[]>(API_ENDPOINTS.TYPES, params),
    get: ({ slug }: { slug: string }) =>
      HttpClient.get<Type>(`${API_ENDPOINTS.TYPES}/${slug}`),
  };
  shops = {
    all: (params: Partial<ShopQueryOptions>) =>
      HttpClient.get<ShopPaginator>(API_ENDPOINTS.SHOPS, {
        search: HttpClient.formatSearchParams({}),
        page: 1,
        ...params,
      }),
    get: (slug: string) =>
      HttpClient.get<Shop>(`${API_ENDPOINTS.SHOPS}/${slug}`),

    searchNearShops: (input: ShopMapLocation) =>
      HttpClient.get<any>(API_ENDPOINTS.NEAR_SHOPS, input),

    getSearchNearShops: ({ lat, lng }: ShopMapLocation) =>
      HttpClient.get<any>(`${API_ENDPOINTS.NEAR_SHOPS}/${lat}/${lng}`),

    shopMaintenanceEvent: ({
      shop_id,
      isMaintenance,
      isShopUnderMaintenance,
    }: ShopMaintenanceEvent) =>
      HttpClient.post<Shop>(API_ENDPOINTS.SHOP_MAINTENANCE_EVENT, {
        shop_id,
        isMaintenance,
        isShopUnderMaintenance,
      }),
  };
  storeNotice = {
    all: ({ shop_id, shops, ...params }: Partial<StoreNoticeQueryOptions>) => {
      return HttpClient.get<StoreNoticePaginator>(API_ENDPOINTS.STORE_NOTICES, {
        searchJoin: 'and',
        shop_id: shop_id,
        ...params,
        search: HttpClient.formatSearchParams({ shop_id, shops }),
      });
    },
  };
  authors = {
    all: ({ name, ...params }: Partial<AuthorQueryOptions>) => {
      return HttpClient.get<AuthorPaginator>(API_ENDPOINTS.AUTHORS, {
        ...params,
        searchJoin: 'and',
        search: HttpClient.formatSearchParams({
          name,
        }),
      });
    },
    top: ({ type, ...params }: Partial<AuthorQueryOptions>) =>
      HttpClient.get<Author[]>(API_ENDPOINTS.AUTHORS_TOP, {
        ...params,
        search: HttpClient.formatSearchParams({
          type,
        }),
      }),
    get: ({ slug, language }: { slug: string; language?: string }) =>
      HttpClient.get<Author>(`${API_ENDPOINTS.AUTHORS}/${slug}`, {
        language,
      }),
  };
  manufacturers = {
    all: ({ name, type, ...params }: Partial<ManufacturerQueryOptions>) =>
      HttpClient.get<ManufacturerPaginator>(API_ENDPOINTS.MANUFACTURERS, {
        ...params,
        page: 1,
        search: HttpClient.formatSearchParams({
          name,
          type,
        }),
      }),
    top: ({ type, ...params }: Partial<ManufacturerQueryOptions>) =>
      HttpClient.get<Manufacturer[]>(API_ENDPOINTS.MANUFACTURERS_TOP, {
        ...params,
        search: HttpClient.formatSearchParams({
          type,
        }),
      }),
    get: ({ slug, language }: { slug: string; language?: string }) =>
      HttpClient.get<Manufacturer>(`${API_ENDPOINTS.MANUFACTURERS}/${slug}`, {
        language,
      }),
  };
  institutionalPages = {
    all: (params?: Partial<{ limit: number; page: number; search: string }>) =>
      HttpClient.get<InstitutionalPagePaginator | InstitutionalPage[]>(
        API_ENDPOINTS.INSTITUTIONAL_PAGES,
        {
          ...params,
          search: params?.search ?? 'status:published',
        },
      ),
    get: ({ slug, preview }: { slug: string; preview?: string }) =>
      HttpClient.get<InstitutionalPage>(
        `${API_ENDPOINTS.INSTITUTIONAL_PAGES}/${slug}`,
        preview ? { preview } : undefined,
      ),
  };
  deliveryPartners = {
    formOptions: () =>
      HttpClient.get<DeliveryPartnerFormOptions>(
        API_ENDPOINTS.DELIVERY_PARTNERS_FORM_OPTIONS,
      ),
    create: (input: CreateDeliveryPartnerInput) =>
      HttpClient.post<CreateDeliveryPartnerResponse>(
        API_ENDPOINTS.DELIVERY_PARTNERS,
        input,
      ),
  };
  coupons = {
    all: (params: Partial<CouponQueryOptions>) =>
      HttpClient.get<CouponPaginator>(API_ENDPOINTS.COUPONS, params),
    verify: (input: VerifyCouponInputType) =>
      HttpClient.post<VerifyCouponResponse>(
        API_ENDPOINTS.COUPONS_VERIFY,
        input,
      ),
    available: (input: { order_items: VerifyCouponInputType['order_items'] }) =>
      HttpClient.post<AvailableCouponsResponse>(
        API_ENDPOINTS.COUPONS_AVAILABLE,
        input,
      ),
  };
  // TODO>: refactor and improve / review if "with" is needed
  orders = {
    all: (req: { filters: Partial<OrderQueryOptions>; params: any }) =>
      HttpClient.get<OrderPaginator>(API_ENDPOINTS.ORDERS, {
        with: 'payment_status;order_status;order_items.product',
        ...(req?.filters && {
          search: HttpClient.formatSearchParams({ ...req?.filters }),
        }),
        ...req?.params,
      }),
    get: (tracking_number: string) =>
      HttpClient.get<Order>(
        `${API_ENDPOINTS.ORDERS_TRACKING_NUMBER}/${tracking_number}`,
        {
          with: 'refund;reviews',
        },
      ),
    create: (input: CreateOrderInput) =>
      HttpClient.post<Order>(API_ENDPOINTS.ORDERS, input),
    purchasedProducts: (params: { page: number; limit: number }) =>
      HttpClient.get<PurchasedProductPaginator>(
        API_ENDPOINTS.ORDERS_PURCHASED_PRODUCTS,
        params,
      ),
    refunds: (params: Pick<QueryOptions, 'limit'>) =>
      HttpClient.get<RefundPaginator>(API_ENDPOINTS.ORDERS_REFUNDS, {
        with: 'refund_policy;order',
        ...params,
      }),
    createRefund: (input: CreateRefundInput) =>
      HttpClient.post<Refund>(API_ENDPOINTS.ORDERS_REFUNDS, input),
    payment: (input: CreateOrderPaymentInput) =>
      HttpClient.post<any>(API_ENDPOINTS.ORDERS_PAYMENT, input),
    savePaymentMethod: (input: any) =>
      HttpClient.post<any>(API_ENDPOINTS.SAVE_PAYMENT_METHOD, input),

    downloadable: (query?: OrderQueryOptions) =>
      HttpClient.get<DownloadableFilePaginator>(
        API_ENDPOINTS.ORDERS_DOWNLOADS,
        query,
      ),
    verify: (input: CheckoutVerificationInput) =>
      HttpClient.post<VerifiedCheckoutData>(
        API_ENDPOINTS.ORDERS_CHECKOUT_VERIFY,
        input,
      ),
    generateDownloadLink: (input: { digital_file_id: string }) =>
      HttpClient.post<string>(
        API_ENDPOINTS.GENERATE_DOWNLOADABLE_PRODUCT_LINK,
        input,
      ),
    getReview: (orderId: number | string) =>
      HttpClient.get<OrderReview>(`${API_ENDPOINTS.ORDERS}/${orderId}/review`),
    createReview: (
      orderId: number | string,
      input: CreateOrderReviewInput,
    ) =>
      HttpClient.post<OrderReview>(
        `${API_ENDPOINTS.ORDERS}/${orderId}/review`,
        input,
      ),
    getPaymentIntentOriginal: ({
      tracking_number,
    }: {
      tracking_number: string;
    }) =>
      HttpClient.get<PaymentIntentCollection>(API_ENDPOINTS.PAYMENT_INTENT, {
        tracking_number,
      }),
    getPaymentIntent: ({
      tracking_number,
      payment_gateway,
      recall_gateway,
    }: {
      tracking_number: string;
      payment_gateway?: string;
      recall_gateway?: boolean;
    }) =>
      HttpClient.get<PaymentIntentCollection>(API_ENDPOINTS.PAYMENT_INTENT, {
        tracking_number,
        payment_gateway,
        recall_gateway,
      }),
  };
  refundReason = {
    all: ({ type, ...params }: Partial<RefundQueryOptions>) =>
      HttpClient.get<RefundReasonPaginator>(API_ENDPOINTS.REFUNDS_REASONS, {
        searchJoin: 'and',
        ...params,
        ...(type && { search: HttpClient.formatSearchParams({ type }) }),
      }),
  };
  users = {
    me: () => HttpClient.get<User>(API_ENDPOINTS.USERS_ME),
    update: (user: UpdateUserInput) =>
      HttpClient.put<User>(API_ENDPOINTS.USERS_UPDATE_PROFILE, user),
    login: (input: LoginUserInput) =>
      HttpClient.post<AuthResponse>(API_ENDPOINTS.USERS_LOGIN, input),
    socialLogin: (input: SocialLoginInputType) =>
      HttpClient.post<AuthResponse>(API_ENDPOINTS.SOCIAL_LOGIN, input),
    googleMobileLogin: (input: GoogleMobileLoginInputType) =>
      HttpClient.post<AuthResponse>(API_ENDPOINTS.GOOGLE_MOBILE_LOGIN, input),
    appleMobileLogin: (input: AppleMobileLoginPayload) =>
      HttpClient.post<AppleMobileLoginResponse>(
        API_ENDPOINTS.APPLE_MOBILE_LOGIN,
        input,
      ),
    sendOtpCode: (input: SendOtpCodeInputType) =>
      HttpClient.post<OTPResponse>(API_ENDPOINTS.SEND_OTP_CODE, input),
    verifyOtpCode: (input: VerifyOtpInputType) =>
      HttpClient.post<OTPVerifyResponse>(API_ENDPOINTS.VERIFY_OTP_CODE, input),
    OtpLogin: (input: OtpLoginInputType) =>
      HttpClient.post<AuthResponse>(API_ENDPOINTS.OTP_LOGIN, input),
    register: (input: RegisterUserOutput) =>
      HttpClient.post<AuthResponse>(API_ENDPOINTS.USERS_REGISTER, input),
    getResetPasswordOptions: (input: { cpf: string }) =>
      HttpClient.post<ResetPasswordOptionsResponse>(
        API_ENDPOINTS.USERS_RESET_PASSWORD_OPTIONS,
        input,
      ),
    forgotPassword: (input: SendResetPasswordInput) =>
      HttpClient.post<PasswordChangeResponse>(
        API_ENDPOINTS.USERS_FORGOT_PASSWORD,
        input,
      ),
    /** Temporary: email-only forgot password (original flow). */
    forgotPasswordWithEmail: (input: ForgotPasswordUserInput) =>
      HttpClient.post<PasswordChangeResponse>(
        API_ENDPOINTS.USERS_FORGOT_PASSWORD,
        input,
      ),
    verifyForgotPasswordToken: (input: VerifyForgotPasswordUserInput) =>
      HttpClient.post<PasswordChangeResponse>(
        API_ENDPOINTS.USERS_VERIFY_FORGOT_PASSWORD_TOKEN,
        input,
      ),
    validateResetToken: (input: ValidateResetTokenInput) =>
      HttpClient.post<ValidateResetTokenResponse>(
        API_ENDPOINTS.USERS_VALIDATE_RESET_TOKEN,
        input,
      ),
    resetPassword: (input: ResetPasswordUserInput) =>
      HttpClient.post<PasswordChangeResponse>(
        API_ENDPOINTS.USERS_RESET_PASSWORD,
        input,
      ),
    changePassword: (input: ChangePasswordUserInput) =>
      HttpClient.post<PasswordChangeResponse>(
        API_ENDPOINTS.USERS_CHANGE_PASSWORD,
        input,
      ),
    updateEmail: (input: UpdateEmailUserInput) =>
      HttpClient.post<EmailChangeResponse>(
        API_ENDPOINTS.USERS_UPDATE_EMAIL,
        input,
      ),
    logout: () => HttpClient.post<boolean>(API_ENDPOINTS.USERS_LOGOUT, {}),
    createAddress: (input: { input: any }) =>
      HttpClient.post<any>(`${API_ENDPOINTS.ADDRESSES}`, input),
    updateAddress: ({ id, address }: { id: any; address: any }) =>
      HttpClient.put<any>(`${API_ENDPOINTS.ADDRESSES}/${id}`, { address }),
    deleteAddress: ({ id, address }: { id: any; address: any }) =>
      HttpClient.put<any>(`${API_ENDPOINTS.ADDRESSES}/${id}`, { address }),
    subscribe: (input: { name: string; email: string }) =>
      axios.post<any>(
        `/api/${API_ENDPOINTS.USERS_SUBSCRIBE_TO_NEWSLETTER}`,
        input,
      ),
    contactUs: (input: CreateContactUsInput) =>
      HttpClient.post<any>(API_ENDPOINTS.USERS_CONTACT_US, input),
    resendVerificationEmail: () => {
      return HttpClient.post<VerificationEmailUserInput>(
        API_ENDPOINTS.SEND_VERIFICATION_EMAIL,
        {},
      );
    },
    generateReferrerCode: () => {
      return HttpClient.post<{ referrer_code: string }>(
        API_ENDPOINTS.USER_PROFILE_GENERATE_REFERRER_CODE,
        {},
      );
    },
  };
  user_relationships = {
    all: () =>
      HttpClient.get<CreateUserRelationshipInput[]>(
        API_ENDPOINTS.USERS_RELATIONSHIPS,
        {},
      ),
    create: (params: CreateUserRelationshipInput) =>
      HttpClient.post(API_ENDPOINTS.USERS_RELATIONSHIPS, { ...params }),
    update: (params: CreateUserRelationshipInput, id?: number) =>
      HttpClient.patch(`${API_ENDPOINTS.USERS_RELATIONSHIPS}/${id}`, {
        ...params,
      }),
    delete: (id: number) =>
      HttpClient.delete(`${API_ENDPOINTS.USERS_RELATIONSHIPS}/${id}`),
  };
  wishlist = {
    all: (params: WishlistQueryOptions) =>
      HttpClient.get<WishlistPaginator>(API_ENDPOINTS.USERS_WISHLIST, {
        with: 'product',
        orderBy: 'created_at',
        sortedBy: 'desc',
        ...params,
      }),
    toggle: (input: { product_id: string; language?: string }) =>
      HttpClient.post<{ in_wishlist: boolean }>(
        API_ENDPOINTS.USERS_WISHLIST_TOGGLE,
        input,
      ),
    remove: (product_id: string | number) =>
      HttpClient.post<Wishlist>(`${API_ENDPOINTS.WISHLIST}/toggle`, {
        product_id: product_id,
      }),
    checkIsInWishlist: ({ product_id }: { product_id: string }) =>
      HttpClient.get<boolean>(
        `${API_ENDPOINTS.WISHLIST}/in_wishlist/${product_id}`,
      ),
  };
  cart = {
    sync: (body: {
      items: Array<{
        product_id: number;
        sku: string;
        quantity: number;
        last_modified_at: string;
        metadata?: Record<string, unknown>;
      }>;
    }) => HttpClient.post<{ ok: true }>(`${API_ENDPOINTS.CART}/sync`, body),
    get: () =>
      HttpClient.get<{
        items: Array<{
          product_id: number;
          sku: string;
          quantity: number;
          last_modified_at: string;
          metadata?: Record<string, unknown> | null;
        }>;
      }>(API_ENDPOINTS.CART),
    delete: () => HttpClient.delete<{ ok: true }>(API_ENDPOINTS.CART),
  };
  settings = {
    all: (params?: SettingsQueryOptions) =>
      HttpClient.get<Settings>(API_ENDPOINTS.SETTINGS, { ...params }),
    upload: (input: File[]) => {
      let formData = new FormData();
      input.forEach((attachment) => {
        formData.append('attachment[]', attachment);
      });
      return HttpClient.post<Attachment[]>(API_ENDPOINTS.UPLOADS, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    deleteAttachment: (id: number) => {
      return HttpClient.delete(`${API_ENDPOINTS.UPLOADS}/${id}`);
    },
    getGeneral: () => HttpClient.get(API_ENDPOINTS.GENERAL_SETTINGS),
    paymentGateways: () =>
      HttpClient.get<
        Array<{
          id: number;
          name: string;
          slug: string;
          active: boolean;
          options?: { discountPercentage?: number };
        }>
      >(API_ENDPOINTS.PAYMENT_GATEWAYS),
  };
  cards = {
    all: (params?: any) =>
      HttpClient.get<ICreditCard[]>(API_ENDPOINTS.CARDS, { ...params }),
    remove: ({ id }: { id: string }) =>
      HttpClient.delete<any>(`${API_ENDPOINTS.CARDS}/${id}`),
    addPaymentMethod: (method_key: any) =>
      HttpClient.post<any>(API_ENDPOINTS.CARDS, method_key),
    makeDefaultPaymentMethod: (input: any) =>
      HttpClient.post<any>(API_ENDPOINTS.SET_DEFAULT_CARD, input),
  };

  faqs = {
    // all: (params?: any) =>
    //   HttpClient.get<FAQS[]>(API_ENDPOINTS.FAQS, { ...params }),
    all: ({ faq_type, issued_by, ...params }: Partial<FaqsQueryOptions>) =>
      HttpClient.get<FaqsPaginator>(API_ENDPOINTS.FAQS, {
        ...params,
        search: HttpClient.formatSearchParams({
          faq_type,
          issued_by,
        }),
      }),
    get: (id: string) => HttpClient.get<FAQS>(`${API_ENDPOINTS.FAQS}/${id}`),
  };

  termsAndConditions = {
    // all: (params?: any) =>
    //   HttpClient.get<FAQS[]>(API_ENDPOINTS.FAQS, { ...params }),
    all: ({
      type,
      issued_by,
      ...params
    }: Partial<TermsAndConditionsQueryOptions>) =>
      HttpClient.get<TermsAndConditionsPaginator>(
        API_ENDPOINTS.TERMS_AND_CONDITIONS,
        {
          searchJoin: 'and',
          ...params,
          search: HttpClient.formatSearchParams({
            type,
            issued_by,
          }),
        },
      ),
    get: (id: string) =>
      HttpClient.get<FAQS>(`${API_ENDPOINTS.TERMS_AND_CONDITIONS}/${id}`),
  };
  flashSale = {
    // all: (params?: any) =>
    //   HttpClient.get<FAQS[]>(API_ENDPOINTS.FAQS, { ...params }),
    all: ({ ...params }: Partial<FlashSaleQueryOptions>) =>
      HttpClient.get<FlashSalePaginator>(API_ENDPOINTS.FLASH_SALE, {
        ...params,
      }),
    get: ({ slug, language }: { slug: string; language?: string }) => {
      return HttpClient.get<FlashSale>(`${API_ENDPOINTS.FLASH_SALE}/${slug}`, {
        language,
        with: 'products',
      });
    },
    getProductsByFlashSale: ({
      slug,
      ...params
    }: FlashSaleProductsQueryOptions) => {
      return HttpClient.get<ProductPaginator>(
        API_ENDPOINTS.PRODUCTS_BY_FLASH_SALE,
        {
          searchJoin: 'and',
          slug,
          ...params,
        },
      );
    },
  };

  refundPolicies = {
    all: ({
      title,
      status,
      target,
      ...params
    }: Partial<RefundPolicyQueryOptions>) =>
      HttpClient.get<RefundPolicyPaginator>(API_ENDPOINTS.REFUND_POLICIES, {
        searchJoin: 'and',
        ...params,
        search: HttpClient.formatSearchParams({
          title,
          target,
          status,
        }),

        with: 'shop;refunds',
      }),
  };
  notifyLogs = {
    all: (params: Partial<NotifyLogsQueryOptions>) =>
      HttpClient.get<NotifyLogsPaginator>(API_ENDPOINTS.NOTIFY_LOGS, {
        ...params,
      }),

    get: ({ id, language }: { id: string; language?: string }) => {
      return HttpClient.get<NotifyLogs>(`${API_ENDPOINTS.NOTIFY_LOGS}/${id}`, {
        language,
      });
    },
    readNotifyLog: (input: { id: string }) =>
      HttpClient.post<NotifyLogs>(API_ENDPOINTS.READ_NOTIFY_LOG, input),
    readAllNotifyLogs: ({ ...params }: Partial<NotifyLogsQueryOptions>) => {
      return HttpClient.post<any>(API_ENDPOINTS.READ_ALL_NOTIFY_LOG, {
        ...params,
      });
    },
  };
  notifications = {
    list: (params?: { page?: number; limit?: number }) =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS, params),
    unreadCount: () =>
      HttpClient.get<{ count: number }>(
        API_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT,
      ),
    markRead: (id: string) =>
      HttpClient.post(`${API_ENDPOINTS.NOTIFICATIONS}/${id}/read`, {}),
    markAllRead: () =>
      HttpClient.post(API_ENDPOINTS.NOTIFICATIONS_READ_ALL, {}),
    dismiss: (id: string) =>
      HttpClient.post(`${API_ENDPOINTS.NOTIFICATIONS}/${id}/dismiss`, {}),
  };
  becomeSeller = {
    get: ({ language }: Pick<QueryOptions, 'language'>) => {
      return HttpClient.get<BecomeSeller>(API_ENDPOINTS.BECAME_SELLER, {
        language,
      });
    },
  };
  api = {
    getShippingMethods: ({ cep, ...params }: any) => {
      return HttpClient.get(API_ENDPOINTS.SHIPPING_METHODS, {
        cep,
        ...params,
      });
    },
  };
  pbm = {
    verify: (input: { product_id: string; cpf: string }) => {
      return HttpClient.post<any>(API_ENDPOINTS.PBM_VERIFY, input);
    },
    activate: (input: any) => {
      return HttpClient.post<any>(API_ENDPOINTS.PBM_ACTIVATE, input);
    },
    verifyPrices: (input: any) => {
      return HttpClient.post<any>(API_ENDPOINTS.PBM_VERIFY_PRICES, input);
    },
    cartVerify: (input: any) => {
      return HttpClient.post<any>(API_ENDPOINTS.CART_VERIFY, input);
    },
  };
  events = {
    track: (input: {
      event_type: string;
      product_id: number;
      session_id: string;
      metadata?: Record<string, unknown>;
    }) => HttpClient.post<void>(API_ENDPOINTS.EVENTS_TRACK, input),
  };
  recommendations = {
    trending: (params: {
      session_id: string;
      limit?: number;
      context?: 'type' | 'category';
      id?: number;
    }) =>
      HttpClient.get<RecommendationsTrendingResponse>(
        API_ENDPOINTS.RECOMMENDATIONS_TRENDING,
        params,
      ),
    lastSeen: (params: { session_id: string; limit?: number }) =>
      HttpClient.get<RecommendationsLastSeenItem[]>(
        API_ENDPOINTS.RECOMMENDATIONS_LAST_SEEN,
        params,
      ),
    fbt: (productId: number) =>
      HttpClient.get<RecommendationsFbtCabResponse>(
        `${API_ENDPOINTS.RECOMMENDATIONS_FBT}/${productId}`,
      ),
    cab: (productId: number) =>
      HttpClient.get<RecommendationsFbtCabResponse>(
        `${API_ENDPOINTS.RECOMMENDATIONS_CAB}/${productId}`,
      ),
  };
}

export type RecommendationsTrendingResponse = {
  context: string;
  context_id: number | null;
  window_days: number;
  computed_at: string;
  cache_miss: boolean;
  products: Array<{
    id: number;
    name: string;
    slug: string;
    price: number;
    image_url: string;
    category_id: number;
    type_id?: number;
    types_id?: number[];
  }>;
};

export type RecommendationsLastSeenItem = {
  id: number;
  name: string;
  slug: string;
  sku: string;
  url: string;
  image: string;
  price: number;
  sale_price: number;
};

export type RecommendationsFbtCabResponse = {
  product_id: number;
  source: string;
  label: string;
  computed_at: string;
  cache_miss: boolean;
  products: Array<{
    id: number;
    name: string;
    slug: string;
    price: number;
    image_url: string;
    category_id: number;
    type_id?: number;
    types_id?: number[];
  }>;
};

const client = new Client();

export default client;
